import { useState } from 'react';
import { useAudit } from '@/app/hooks';
import { useAuth } from '@/app/AuthContext';
import { PageHeader } from '@/components/PageHeader';
import { Card } from '@/components/Card';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Forbidden } from '@/components/PermissionGate';
import { Spinner } from '@/components/LoadingScreen';
import { Permission } from '@/types/permission';
import { font, v } from '@/theme/tokens';

const ACTION_LABELS: Record<string, string> = {
  // Kept so entries written before the security vocabulary landed still read
  // as words rather than as a raw code.
  'auth.login': 'Вход в систему',
  password_login_success: 'Вход по паролю',
  password_login_failed: 'Неудачная попытка входа',
  passkey_registered: 'Passkey добавлен',
  passkey_login_success: 'Вход по Passkey',
  passkey_login_failed: 'Неудачный вход по Passkey',
  passkey_renamed: 'Passkey переименован',
  passkey_deleted: 'Passkey удалён',
  session_revoked: 'Сессия завершена',
  all_other_sessions_revoked: 'Выход на других устройствах',
  password_changed: 'Пароль изменён',
  rate_limit_triggered: 'Сработало ограничение попыток',
  'import.upload': 'Загрузка отчёта',
  'import.confirm': 'Импорт подтверждён',
  'import.rollback': 'Откат импорта',
  'import.map_sheet': 'Сопоставление листа',
  'import.map_category': 'Сопоставление статьи',
  'period.close': 'Период закрыт',
  'period.reopen': 'Период переоткрыт',
  'adjustment.create': 'Ручная корректировка',
  'comment.create': 'Комментарий',
  'unit.create': 'Создан объект',
};

export function AuditPage() {
  const { can } = useAuth();
  const allowed = can(Permission.VIEW_AUDIT);
  const [action, setAction] = useState('');
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState<string | null>(null);
  // Typing the URL by hand must not fetch a single audit row. The query is
  // disabled outright, and the server refuses the same request anyway.
  const query = useAudit({ action: action || undefined, offset }, allowed);

  if (!allowed) {
    return (
      <div style={{ animation: 'fade-in 0.35s ease' }}>
        <PageHeader title="Журнал аудита" />
        <Forbidden message="Журнал аудита доступен только владельцу." />
      </div>
    );
  }

  const data = query.data;

  return (
    <div style={{ animation: 'fade-in 0.35s ease' }}>
      <PageHeader title="Журнал аудита" subtitle="Все значимые действия. Только чтение — записи неизменяемы." />

      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <select
          value={action}
          aria-label="Фильтр по действию"
          onChange={(e) => { setAction(e.target.value); setOffset(0); }}
          style={{ padding: '8px 11px', border: `1px solid ${v('line')}`, borderRadius: 9, background: v('surface'), color: v('ink'), fontSize: 13, fontFamily: 'inherit' }}
        >
          <option value="">Все действия</option>
          {Object.entries(ACTION_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>

      {query.isPending && <Spinner />}
      {query.error && (
        <ErrorState
          message={query.error instanceof Error ? query.error.message : 'Ошибка загрузки'}
          onRetry={() => void query.refetch()}
        />
      )}

      {data && data.items.length === 0 && (
        <EmptyState title="Записей нет" description="По выбранному фильтру в журнале аудита ничего не найдено." icon="doc" />
      )}

      {data && data.items.length > 0 && (
        <Card padding={0}>
          {data.items.map((entry) => (
            <div key={entry.id} style={{ borderBottom: `1px solid ${v('line-soft')}` }}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => setOpen(open === entry.id ? null : entry.id)}
                onKeyDown={(e) => e.key === 'Enter' && setOpen(open === entry.id ? null : entry.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 16px', cursor: 'pointer', flexWrap: 'wrap' }}
              >
                <span style={{ fontFamily: font.mono, fontSize: 12, color: v('ink-muted'), minWidth: 130 }}>
                  {new Date(entry.created_at).toLocaleString('ru-RU')}
                </span>
                <span style={{ fontWeight: 600, fontSize: 13, minWidth: 140 }}>
                  {entry.actor?.full_name ?? 'Система'}
                </span>
                <Badge tone={entry.action.includes('rollback') || entry.action.includes('reopen') ? 'warning' : 'neutral'}>
                  {ACTION_LABELS[entry.action] ?? entry.action}
                </Badge>
                <span style={{ fontSize: 12.5, color: v('ink-muted') }}>{entry.entity_type}</span>
              </div>
              {open === entry.id && entry.payload && (
                <pre style={{ margin: 0, padding: '4px 16px 14px 158px', fontSize: 12, fontFamily: font.mono, color: v('ink-sub'), whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {JSON.stringify(entry.payload, null, 2)}
                </pre>
              )}
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px' }}>
            <span style={{ fontSize: 12.5, color: v('ink-muted') }}>
              {offset + 1}–{Math.min(offset + 50, data.total)} из {data.total}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 50))}>← Новее</Button>
              <Button disabled={offset + 50 >= data.total} onClick={() => setOffset(offset + 50)}>Старше →</Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
